<script setup lang="ts">
import type {SolutionCardItem} from '~/types/legacy'
import type {Populated} from '~/types/populated'
import {resolveMediaUrl} from '~/utils/relations'

import {cx} from 'class-variance-authority'
import {ref, onMounted, computed} from 'vue'
import RichText from '../common/rich-text.vue'

const props = defineProps<{
  cards: Populated<SolutionCardItem>[]
}>()

const standardClasses = 'h-full rounded-lg relative sm:min-w-[320px] border shadow-sm overflow-hidden'

const windowWidth = ref(0)

onMounted(() => {
  windowWidth.value = window.innerWidth
  window.addEventListener('resize', () => (windowWidth.value = window.innerWidth))
})

const firstThreeCards = computed(() => props.cards.slice(0, 3))
const remainingCards = computed(() => props.cards.slice(3))

const getCardStyle = (card: SolutionCardItem) => ({
  backgroundImage: resolveMediaUrl(card.image)
    ? `url("${resolveMediaUrl(card.image)}")`
    : 'linear-gradient(to bottom, #f9fafb 0%, white 100%)',
  backgroundPosition: 'center top',
  backgroundRepeat: 'no-repeat',
})

const getCardStyle2 = (card: SolutionCardItem) => ({
  backgroundImage: resolveMediaUrl(card.image)
    ? `url("${resolveMediaUrl(card.image)}")`
    : 'linear-gradient(to bottom, #f9fafb 0%, white 100%)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'left',
})
</script>

<template>
  <div class="rounded-lg bg-white">
    <div
      v-if="firstThreeCards.length > 0"
      class="mb-6 grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3"
    >
      <div v-for="card in firstThreeCards" :key="card.id!" class="rounded-lg border p-2">
        <div
          :style="getCardStyle(card)"
          :class="cx(standardClasses, 'col-span-1')"
          class="flex min-h-[390px] flex-col justify-end border"
        >
          <div
            class="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent from-0% via-transparent via-20% to-white to-50%"
          ></div>
          <div
            class="pointer-events-none absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white via-white/80 to-transparent"
          ></div>

          <div class="relative z-10 p-4 sm:p-6">
            <div class="mb-3 flex items-center space-x-3 sm:mb-4 sm:space-x-4">
              <div class="flex-shrink-0 rounded-lg bg-main-600 p-2 text-white sm:p-3">
                <NuxtImg :src="resolveMediaUrl(card.icon)" class="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <Heading size="small" class="text-sm sm:text-base">
                {{ card.title }}
              </Heading>
            </div>
            <RichText :raw="card.list" class="text-xs leading-relaxed text-gray-600 sm:text-sm" />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="remainingCards.length > 0"
      class="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 lg:grid-cols-2"
      style="grid-auto-flow: row"
    >
      <div v-for="(card, index) in remainingCards" :key="index + 3" class="rounded-lg border p-2">
        <div
          :style="getCardStyle2(card)"
          :class="standardClasses"
          class="flex min-h-[300px] flex-col items-stretch border sm:min-h-[200px] sm:flex-row"
        >
          <div class="relative min-h-[150px] flex-1 sm:min-h-full"></div>

          <div
            class="pointer-events-none absolute bottom-0 left-0 right-0 top-0 z-10 bg-gradient-to-t from-transparent from-30% via-white/70 via-70% to-white sm:right-1/3 sm:bg-gradient-to-r"
          ></div>

          <div class="relative z-20 flex flex-1 flex-col justify-center bg-white p-4 sm:p-5">
            <div class="mb-3 flex items-center space-x-3 sm:mb-4 sm:space-x-4">
              <div class="flex-shrink-0 rounded-lg bg-main-600 p-2 text-white sm:p-3">
                <NuxtImg :src="resolveMediaUrl(card.icon)" class="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <Heading size="small" class="text-sm sm:text-base">
                {{ card.title }}
              </Heading>
            </div>
            <RichText :raw="card.list" class="text-xs leading-relaxed text-gray-600 sm:text-sm" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
